import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const MAX_POLLS = 8;   // ~16s
const INTERVAL_MS = 2000;

export default function MyAdminSubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error | notfound
  const [detail, setDetail] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      setTries(attempts);
      try {
        const { data } = await api.get(`/next/payments/status/${sessionId}`);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) setStatus("notfound");
        else if (attempts >= MAX_POLLS) setStatus("timeout");
        else setTimeout(poll, INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="myadmin-subscription-success" className="max-w-2xl">
      <p className="eyebrow">Stripe TEST · Souscription</p>
      <h1 className="font-serif-luxe text-4xl mt-3 mb-6">
        {status === "paid" ? <>Paiement <span className="italic gold-text">confirmé.</span></>
          : status === "polling" ? <>Confirmation en <span className="italic gold-text">cours…</span></>
          : status === "timeout" ? <>Confirmation <span className="italic gold-text">différée.</span></>
          : status === "notfound" ? <>Session <span className="italic gold-text">introuvable.</span></>
          : <>Erreur.</>}
      </h1>

      <div className="card-luxe p-8">
        {status === "polling" && (
          <div className="flex items-center gap-4">
            <Loader2 size={22} className="text-[#DCB457] animate-spin" />
            <div>
              <p className="text-sm text-white/75">Nous vérifions la confirmation Stripe.</p>
              <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">Tentative {tries}/{MAX_POLLS}</p>
            </div>
          </div>
        )}
        {status === "paid" && (
          <div>
            <div className="flex items-center gap-3 text-[#DCB457]">
              <CheckCircle2 size={22} />
              <p className="text-sm">Le forfait est désormais actif pour cet événement.</p>
            </div>
            <dl className="mt-6 grid grid-cols-2 gap-3 text-xs text-white/60">
              <dt className="text-white/40">Statut souscription</dt><dd className="text-[#DCB457]">{detail?.subscription_status}</dd>
              <dt className="text-white/40">Statut paiement</dt><dd className="text-[#DCB457]">{detail?.payment_status}</dd>
              <dt className="text-white/40">Session ID</dt><dd className="break-all">{sessionId}</dd>
            </dl>
          </div>
        )}
        {status === "timeout" && (
          <div>
            <p className="text-sm text-white/75">La confirmation prend plus de temps que prévu. Le paiement sera finalisé automatiquement dès que Stripe nous notifiera.</p>
            <p className="text-xs text-white/50 mt-2">Session : <span className="break-all">{sessionId}</span></p>
          </div>
        )}
        {status === "notfound" && (
          <div className="flex items-center gap-3 text-red-300">
            <XCircle size={22} />
            <p className="text-sm">Session introuvable. Contactez le support si le paiement a été prélevé.</p>
          </div>
        )}
      </div>

      <div className="mt-6 flex gap-3">
        <Link to="/admin/events" data-testid="back-to-events"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#DCB457]/40 text-[#DCB457] hover:bg-[#DCB457]/10 text-xs uppercase tracking-widest">
          Retour aux événements <ArrowRight size={13} />
        </Link>
        <Link to="/admin/payments"
          className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/60 hover:text-white text-xs uppercase tracking-widest">
          Voir les paiements
        </Link>
      </div>
    </div>
  );
}
